import React, { useState, useRef, useEffect } from 'react';
import { IoMdArrowDropdown, IoMdMenu } from 'react-icons/io';
import '../../../assets/css/App.css';
import 'utils/loadingSpinner.css'; // Import the spinner CSS
import qualbotTextImage from '../images/Qual-logo.png';
import AxiosInstance from '../../../axiosConfig';

const NavBar = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [formData, setFormData] = useState({
    botName: '',
    botPurpose: '',
    yourName: '',
    companyEmail: '',
    companyName: '',
    botLanguage: '',
  });
  const [errors, setErrors] = useState({});
  const [isCreatingBot, setIsCreatingBot] = useState(false); // Track /create_bot API call
  const [apiMessage, setApiMessage] = useState('');

  const solutionsRef = useRef(null);
  const resourcesRef = useRef(null);
  const companyRef = useRef(null);
  const popupRef = useRef(null);

  const top_15_languages = {
    en: 'English',
    zh: 'Chinese (Mandarin)',
    hi: 'Hindi',
    es: 'Spanish',
    fr: 'French',
    ar: 'Arabic',
    bn: 'Bengali',
    ru: 'Russian',
    pt: 'Portuguese',
    id: 'Indonesian',
    ur: 'Urdu',
    de: 'German',
    ja: 'Japanese',
    pa: 'Punjabi',
    sw: 'Swahili',
  };

  const publicEmailRegex = /@(gmail|yahoo|hotmail|outlook|aol|protonmail|icloud|mail|zoho|yandex|gmx|live|msn|rocketmail|rediffmail|inbox|fastmail|hushmail|lycos|example|company)\./i;
  const consonantRegex = /[bcdfghjklmnpqrstvwxyz]{6,}/i;
  const botNameRegex = /^[a-zA-Z0-9]+(_[a-zA-Z0-9]+)*$/;

  const validateEmailDomain = async (email) => {
    try {
      const domain = email.split('@')[1];
      if (!domain) return false;
      const response = await AxiosInstance.get('/validate_domain', {
        params: { domain },
        timeout: 3000,
      });
      const data = response.data;
      return data.Answer?.length > 0;
    } catch (error) {
      console.error('Domain validation failed:', error);
      return false;
    }
  };

  const validateForm = async () => {
    const newErrors = {};

    // Bot Name: Required, underscore-separated, no 6+ consonants
    if (!formData.botName) {
      newErrors.botName = 'Bot Name is required';
    } else if (!botNameRegex.test(formData.botName)) {
      newErrors.botName = 'Bot Name must be one word or words joined by underscores (e.g., WallStreetBot)';
    } else if (consonantRegex.test(formData.botName)) {
      newErrors.botName = 'cons: Bot Name classified as likely spam';
    }

    // Bot Purpose: Required, ≥10 words, no 6+ consonants
    const wordCount = formData.botPurpose.trim().split(/\s+/).filter(word => word.length > 0).length;
    if (!formData.botPurpose) {
      newErrors.botPurpose = 'Bot Purpose is required';
    } else if (wordCount < 10) {
      newErrors.botPurpose = 'Bot Purpose must be well-defined with at least 10 words';
    } else if (consonantRegex.test(formData.botPurpose)) {
      newErrors.botPurpose = 'cons: Bot purpose classified as likely spam';
    }

    // Your Name: Required, no 6+ consonants
    if (!formData.yourName) {
      newErrors.yourName = 'Your Name is required';
    } else if (consonantRegex.test(formData.yourName)) {
      newErrors.yourName = 'cons: Your Name classified as invalid and likely spam';
    }

    // Company Email: Required, no public domains, no 6+ consonants, valid domain
    if (!formData.companyEmail) {
      newErrors.companyEmail = 'Company Email is required';
    } else if (publicEmailRegex.test(formData.companyEmail)) {
      newErrors.companyEmail = 'Please use a company or organisation email, not a personal email';
    } else {
      const localPart = formData.companyEmail.split('@')[0];
      if (consonantRegex.test(localPart)) {
        newErrors.companyEmail = 'Bot sensed this as a spam email attempt. Use a valid company email';
      } else {
        const isDomainValid = await validateEmailDomain(formData.companyEmail);
        if (!isDomainValid) {
          newErrors.companyEmail = 'This email is likely unreachable. Use a valid email';
        }
      }
    }

    // Company Name: Required, no 6+ consonants
    if (!formData.companyName) {
      newErrors.companyName = 'Company Name is required';
    } else if (consonantRegex.test(formData.companyName)) {
      newErrors.companyName = 'Company Name contains too many consecutive consonants, likely spam';
    }

    // Bot Language: Required, from top_15_languages
    if (!formData.botLanguage) {
      newErrors.botLanguage = 'Bot Primary Language is required';
    } else if (!Object.values(top_15_languages).includes(formData.botLanguage)) {
      newErrors.botLanguage = 'Please select a valid language from the list';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMenuClick = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handlePopupToggle = () => {
    setIsPopupOpen(!isPopupOpen);
    if (!isPopupOpen) {
      setErrors({});
      setApiMessage('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleCreateBot = async (e) => {
    e.preventDefault();
    if (await validateForm()) {
      setIsCreatingBot(true); // Start spinner for /create_bot
      try {
        const response = await AxiosInstance.post('/create_bot', formData);
        setApiMessage(response.data.message || 'Bot created successfully');
        setFormData({
          botName: '',
          botPurpose: '',
          yourName: '',
          companyEmail: '',
          companyName: '',
          botLanguage: '',
        });
        setErrors({});
      } catch (error) {
        console.error('Error creating bot:', error);
        setApiMessage(error.response?.data?.message || 'An error occurred while creating the bot.');
      } finally {
        setIsCreatingBot(false); // Stop spinner after /create_bot completes
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        ![solutionsRef.current, resourcesRef.current, companyRef.current, popupRef.current].some(
          (ref) => ref && ref.contains(event.target)
        )
      ) {
        setOpenMenu(null);
        if (isPopupOpen && !popupRef.current.contains(event.target)) {
          setIsPopupOpen(false);
          setErrors({});
          setApiMessage('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPopupOpen]);

  return (
    <div className={`sticky top-0 z-50 bg-${isDarkMode ? '[#cbd5e0]' : 'white'} text-${isDarkMode ? 'white' : 'black'}`}>
      <div className="max-w-screen-xl mx-auto px-4 py-2 md:py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <img
            src={qualbotTextImage}
            alt="QualBot logo"
            className="logo w-60 md:w-65 h-24 md:h-28 object-cover object-center"
          />
        </div>

        <button onClick={handleMobileMenuToggle} className="md:hidden text-lg">
          <IoMdMenu className="w-6 h-6 md:w-8 md:h-8" />
        </button>

        <div
          className={`${
            isMobileMenuOpen ? 'flex' : 'hidden'
          } flex-col md:flex md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-6 ml-auto bg-${
            isDarkMode ? '0b1437' : 'white'
          } md:bg-transparent w-full md:w-auto absolute md:static top-14 left-0 md:top-0 px-4 md:px-0`}
        >
          <div className="relative" ref={solutionsRef}>
            <p
              className={`text-lg md:text-xl font-bold cursor-pointer text-gray-800 hover:text-[#f0a500]`}
              onClick={() => handleMenuClick('solutions')}
            >
              Services
            </p>
          </div>

          <div className="relative flex flex-row items-center" ref={resourcesRef}>
            <div
              className={`flex items-center text-lg md:text-xl font-bold cursor-pointer text-gray-800 hover:text-[#f0a500]`}
              onClick={() => handleMenuClick('resources')}
            >
              <p className="mb-0">Resources</p>
              <IoMdArrowDropdown className="ml-1" />
            </div>
            {openMenu === 'resources' && (
              <div
                className={`absolute top-full left-0 bg-${
                  isDarkMode ? '0b1437' : 'white'
                } text-gray-800 border border-gray-300 rounded-lg shadow-lg mt-2 z-50 min-w-[200px]`}
              >
                <p className="p-3 cursor-pointer hover:text-[#f0a500] hover:font-bold">Testimonial Videos</p>
                <p className="p-3 cursor-pointer hover:text-[#f0a500] hover:font-bold">Success Stories</p>
                <p className="p-3 cursor-pointer hover:text-[#f0a500] hover:font-bold">Developer Portal</p>
              </div>
            )}
          </div>

          <div className="relative flex flex-row items-center" ref={companyRef}>
            <div
              className={`flex items-center text-lg md:text-xl font-bold cursor-pointer text-gray-800 hover:text-[#f0a500]`}
              onClick={() => handleMenuClick('company')}
            >
              <p className="mb-0">Company</p>
              <IoMdArrowDropdown className="ml-1" />
            </div>
            {openMenu === 'company' && (
              <div
                className={`absolute top-full left-0 bg-${
                  isDarkMode ? '0b1437' : 'white'
                } text-gray-800 border border-gray-300 rounded-lg shadow-lg mt-2 z-50 min-w-[150px]`}
              >
                <p className="p-3 cursor-pointer hover:text-[#f0a500] hover:font-bold">About Us</p>
                <p className="p-3 cursor-pointer hover:text-[#f0a500] hover:font-bold">Careers</p>
              </div>
            )}
          </div>

          <div className="relative pt-2 md:pt-0">
            <a
              href="/auth/signIn"
              className="border border-[#0b1437] bg-[#0b1437] text-white py-2 px-4 rounded-lg text-base md:text-lg hover:bg-[#f0a500] hover:text-[#0b1437] transition-colors duration-300"
            >
              Log In
            </a>
          </div>

          <div className="relative pt-2 md:pt-0">
            <button
              onClick={handlePopupToggle}
              className={`bg-gradient-to-r from-[#f0a500] to-[#d97706] text-[#0b1437] py-2 px-6 rounded-lg text-base md:text-lg font-bold hover:from-[#d97706] hover:to-[#f0a500] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#f0a500] ${
                isCreatingBot ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              aria-label="Create BOT for Free"
              disabled={isCreatingBot}
            >
              {isCreatingBot ? (
                <span className="flex items-center">
                  <span className="spinner inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></span>
                  Processing request...
                </span>
              ) : (
                'Create BOT'
              )}
            </button>
          </div>
        </div>
      </div>

      {isCreatingBot && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
          <div className="loading-text">Processing request...</div>
        </div>
      )}

      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]" role="dialog" aria-labelledby="create-bot-title">
          <div
            ref={popupRef}
            className="bg-white text-black p-6 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-y-auto"
          >
            <h2 id="create-bot-title" className="text-xl font-bold mb-4 text-center">Create Your BOT</h2>
            <form onSubmit={handleCreateBot} className="space-y-3">
              <div>
                <label htmlFor="botName" className="block text-sm font-medium mb-1">
                  Bot Name
                </label>
                <input
                  type="text"
                  id="botName"
                  name="botName"
                  value={formData.botName}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f0a500]"
                  placeholder="Enter bot name"
                  required
                  aria-required="true"
                />
                {errors.botName && (
                  <p className="text-red-500 text-xs mt-1">{errors.botName}</p>
                )}
              </div>
              <div>
                <label htmlFor="botPurpose" className="block text-sm font-medium mb-1">
                  Bot Purpose
                </label>
                <textarea
                  id="botPurpose"
                  name="botPurpose"
                  value={formData.botPurpose}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f0a500]"
                  rows="3"
                  placeholder="Describe the purpose of your bot"
                  required
                  aria-required="true"
                />
                {errors.botPurpose && (
                  <p className="text-red-500 text-xs mt-1">{errors.botPurpose}</p>
                )}
              </div>
              <div>
                <label htmlFor="yourName" className="block text-sm font-medium mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  id="yourName"
                  name="yourName"
                  value={formData.yourName}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f0a500]"
                  placeholder="Enter your name"
                  required
                  aria-required="true"
                />
                {errors.yourName && (
                  <p className="text-red-500 text-xs mt-1">{errors.yourName}</p>
                )}
              </div>
              <div>
                <label htmlFor="companyEmail" className="block text-sm font-medium mb-1">
                  Company Email
                </label>
                <input
                  type="email"
                  id="companyEmail"
                  name="companyEmail"
                  value={formData.companyEmail}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f0a500]"
                  placeholder="Enter your company email"
                  required
                  aria-required="true"
                />
                {errors.companyEmail && (
                  <p className="text-red-500 text-xs mt-1">{errors.companyEmail}</p>
                )}
              </div>
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f0a500]"
                  placeholder="Enter your company name"
                  required
                  aria-required="true"
                />
                {errors.companyName && (
                  <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>
                )}
              </div>
              <div>
                <label htmlFor="botLanguage" className="block text-sm font-medium mb-1">
                  Bot Primary Language
                </label>
                <input
                  type="text"
                  id="botLanguage"
                  name="botLanguage"
                  value={formData.botLanguage}
                  onChange={handleInputChange}
                  list="languageOptions"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f0a500]"
                  placeholder="Select or type a language"
                  required
                  aria-required="true"
                />
                <datalist id="languageOptions">
                  {Object.values(top_15_languages).map((language) => (
                    <option key={language} value={language} />
                  ))}
                </datalist>
                {errors.botLanguage && (
                  <p className="text-red-500 text-xs mt-1">{errors.botLanguage}</p>
                )}
              </div>
              <div className="flex justify-center mt-4">
                <button
                  type="submit"
                  className="bg-[#0b1437] text-white py-2 px-6 rounded-lg font-bold hover:bg-[#f0a500] hover:text-[#0b1437] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#f0a500] disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Create Bot"
                  disabled={isCreatingBot}
                >
                  {isCreatingBot ? (
                    <span className="flex items-center">
                      <span className="spinner inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></span>
                      Processing request...
                    </span>
                  ) : (
                    'Create Bot'
                  )}
                </button>
              </div>
              {apiMessage && (
                <p className={`text-center mt-4 ${apiMessage.includes('error') ? 'text-red-500' : 'text-green-500'}`}>
                  {apiMessage}
                </p>
              )}
            </form>
            <button
              onClick={handlePopupToggle}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Close popup"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavBar;
